import React from 'react'
import { validUrl } from '@pepper/utils/src/constant/form/form'
import { Attachments, Divider, Tag, FilePreviewV2 } from '../../atoms'
import { format, isValid } from 'date-fns'
import { ExternalLink } from 'react-feather'
import styles from './_.module.css'
import { TAGS } from '@pepper/utils/src/constant/enum/tags'

const getLinks = (link: string) => {
  return (
    <a
      className={styles.link}
      href={validUrl.test(link) ? link : `http://${link}`}
      target="_blank"
      rel="noreferrer"
      title={link}
    >
      {link} <ExternalLink size={14} />
    </a>
  )
}

function isTruthyValue(value: string | number | string[] | Attachment[]) {
  if (value === '1') return true

  return false
}

const LoomVideoThumbnail = ({ loomUrl }: { loomUrl: string }) => {
  return (
    <div className={styles.loomVideoThumbnail}>
      <FilePreviewV2 url={loomUrl} />
    </div>
  )
}

export const getUpdatedDetails = (
  projectFormFields: BackendFormField[],
  type = '',
  title = '',
  values?: {
    key: string
    old_value: string | string[] | Attachment[] | number
    new_value: string | string[] | Attachment[] | number
  }
): React.ReactNode => {
  if (!type || !title || !values) return null

  switch (type) {
    case 'string': {
      const _oldValue = values?.old_value || '--'
      const _newValue = values?.new_value || '--'

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={_oldValue}
          to={_newValue}
        />
      )
    }

    case 'multiple': {
      const _oldValues = values.old_value as string[]
      const _newValues = values.new_value as string[]

      if (title.toLowerCase() === 'tags') {
        return (
          <UpdatedDetailsWrapper
            fieldTitle={title}
            from={
              Array.isArray(_oldValues) && _oldValues.length > 0
                ? _oldValues.map(val => (
                    <Tag key={val} color={TAGS?.[val as TagType]?.color}>
                      {TAGS?.[val as TagType]?.title}
                    </Tag>
                  ))
                : '--'
            }
            to={
              Array.isArray(_newValues) && _newValues.length > 0
                ? _newValues.map(val => (
                    <Tag key={val} color={TAGS?.[val as TagType]?.color}>
                      {TAGS?.[val as TagType]?.title}
                    </Tag>
                  ))
                : '--'
            }
          />
        )
      }

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={
            Array.isArray(_oldValues)
              ? _oldValues.map(val => <Tag key={val}>{val}</Tag>)
              : '--'
          }
          to={
            Array.isArray(_newValues)
              ? _newValues.map(val => <Tag key={val}>{val}</Tag>)
              : '--'
          }
        />
      )
    }

    case 'links': {
      const _oldValues = values.old_value as string[]
      const _newValues = values.new_value as string[]

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={
            Array.isArray(_oldValues)
              ? _oldValues.map(item => getLinks(item))
              : '--'
          }
          to={
            Array.isArray(_newValues)
              ? _newValues.map(item => getLinks(item))
              : '--'
          }
        />
      )
    }

    case 'link': {
      if (
        typeof values.old_value === 'string' &&
        typeof values.new_value === 'string'
      ) {
        return (
          <UpdatedDetailsWrapper
            fieldTitle={title}
            from={
              !Array.isArray(values.old_value)
                ? getLinks(values.old_value)
                : '--'
            }
            to={
              !Array.isArray(values.new_value)
                ? getLinks(values.new_value)
                : '--'
            }
          />
        )
      }

      return null
    }

    case 'date': {
      const dateFormat = 'dd-MM-yyy | HH:mm'
      let from = '--'
      let to = '--'

      if (
        values.old_value &&
        typeof values.old_value === 'string' &&
        isValid(new Date(values.old_value))
      ) {
        from = format(new Date(values.old_value), dateFormat)
      }

      if (
        values.new_value &&
        typeof values.new_value === 'string' &&
        isValid(new Date(values.new_value))
      ) {
        to = format(new Date(values.new_value), dateFormat)
      }

      return <UpdatedDetailsWrapper fieldTitle={title} from={from} to={to} />
    }

    case 'file': {
      const _oldValue: Attachment[] = values.old_value as Attachment[]
      const _newValue: Attachment[] = values.new_value as Attachment[]

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={
            Array.isArray(_oldValue) ? (
              <Attachments attachments={_oldValue} />
            ) : (
              '--'
            )
          }
          to={
            Array.isArray(_newValue) ? (
              <Attachments attachments={_newValue} />
            ) : (
              '--'
            )
          }
        />
      )
    }

    case 'currency': {
      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={
            values.old_value !== '' ? (values.old_value as number) / 100 : '--'
          }
          to={
            values.new_value !== '' ? (values.new_value as number) / 100 : '--'
          }
        />
      )
    }

    case 'number': {
      if (
        typeof values.old_value !== 'number' ||
        typeof values.new_value !== 'number'
      )
        return null

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={values.old_value || '--'}
          to={values.new_value || '--'}
        />
      )
    }

    case 'multiBlockSelect': {
      if (values?.key === 'language_id') {
        const options =
          (projectFormFields.find(field => field.id === values.key)
            ?.options as BlockSelectOption[]) ?? []

        if (!Array.isArray(options)) {
          return <UpdatedDetailsWrapper fieldTitle={title} from="--" to="--" />
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const oldLanguage: any = options?.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (option: any) => +option.value === +values.old_value
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newLanguage: any = options?.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (option: any) => +option.value === +values.new_value
        )

        return (
          <UpdatedDetailsWrapper
            fieldTitle={title}
            from={oldLanguage?.label || '--'}
            to={newLanguage?.label || '--'}
          />
        )
      }

      const getOptions = projectFormFields?.find(
        field => field.id === values.key
      )?.options as BlockSelectOption[]

      const _oldValue =
        Array.isArray(values.old_value) &&
        (values.old_value as string[]).map((v: string) => {
          if (Array.isArray(getOptions)) {
            return getOptions.find(
              (option: BlockSelectOption) => option.id === v
            )
          }

          return null
        })

      const _newValue =
        Array.isArray(values.new_value) &&
        (values.new_value as string[]).map((v: string) => {
          if (Array.isArray(getOptions)) {
            return getOptions.find(
              (option: BlockSelectOption) => option.id === v
            )
          }

          return null
        })

      return (
        <React.Fragment>
          <div className={styles.title}>{title}</div>
          <div className={styles.detailsData}>
            <div className={styles.label}>From:</div>
            <div className={styles.blocksContainer}>
              {Array.isArray(_oldValue) &&
                _oldValue.map((block, index) => (
                  <div key={index} className={styles.blocks}>
                    <img src={block?.icon} alt={block?.title} />
                  </div>
                ))}
            </div>
          </div>
          <div className={styles.detailsData}>
            <span className={styles.label}>To:</span>
            <div className={styles.blocksContainer}>
              {Array.isArray(_newValue) &&
                _newValue.map((block, index) => (
                  <div key={index} className={styles.blocks}>
                    <img src={block?.icon} alt={block?.title} />
                  </div>
                ))}
            </div>
          </div>
        </React.Fragment>
      )
    }

    case 'customColour': {
      if (!Array.isArray(values.old_value) && !Array.isArray(values.new_value))
        return
      const _oldValue = values.old_value as string[]
      const _newValue = values.new_value as string[]

      return (
        <React.Fragment>
          <div className={styles.title}>{title}</div>
          <div className={styles.detailsData}>
            <div className={styles.label}>From:</div>
            <div className={styles.blocksContainer}>
              {_oldValue?.map((color, index) => (
                <div key={index} className={styles.customColorBox}>
                  <div
                    style={{ backgroundColor: color }}
                    className={styles.colorBlockBox}
                  ></div>
                  <div className={styles.colorCode}>{color}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.detailsData}>
            <div className={styles.label}>To:</div>
            <div className={styles.blocksContainer}>
              {_newValue?.map((color, index) => (
                <div key={index} className={styles.customColorBox}>
                  <div
                    style={{ backgroundColor: color }}
                    className={styles.colorBlockBox}
                  ></div>
                  <div className={styles.colorCode}>{color}</div>
                </div>
              ))}
            </div>
          </div>
        </React.Fragment>
      )
    }

    case 'boolean': {
      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          to={
            values.new_value
              ? isTruthyValue(values.new_value)
                ? 'Yes'
                : 'No'
              : '--'
          }
          from={
            values.old_value
              ? isTruthyValue(values.old_value)
                ? 'Yes'
                : 'No'
              : '--'
          }
        />
      )
    }

    case 'array': {
      if (!Array.isArray(values.old_value) || !Array.isArray(values.new_value))
        return null

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={values.old_value.join(', ')}
          to={values.new_value.join(', ')}
        />
      )
    }

    case 'loomUrls': {
      if (!Array.isArray(values.old_value)) return null
      if (!Array.isArray(values.new_value)) return null

      const _oldValues: string[] = []
      const _newValues: string[] = []

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values.old_value.forEach((oldValue: any) => {
        const url = oldValue?.split(',')?.[1]?.split('link:')?.[1] ?? ''

        _oldValues.push(url)
      })

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values.new_value.forEach((newValue: any) => {
        const url = newValue?.split(',')?.[1]?.split('link:')?.[1] ?? ''

        _newValues.push(url)
      })

      return (
        <UpdatedDetailsWrapper
          fieldTitle={title}
          from={
            _oldValues.length > 0
              ? _oldValues.map(loomUrl => (
                  <LoomVideoThumbnail key={loomUrl} loomUrl={loomUrl} />
                ))
              : '--'
          }
          to={
            _newValues.length > 0
              ? _newValues.map(loomUrl => (
                  <LoomVideoThumbnail key={loomUrl} loomUrl={loomUrl} />
                ))
              : '--'
          }
        />
      )
    }

    default:
      return
  }
}

const UpdatedDetailsWrapper: React.FC<{
  fieldTitle: string
  from: React.ReactNode
  to: React.ReactNode
}> = ({ fieldTitle, from, to }) => {
  return (
    <div>
      <span className={styles.title}>{fieldTitle}</span>
      <div className={styles.detailsData}>
        <span className={styles.label}>From:</span>
        {typeof from === 'string' ? (
          <span
            dangerouslySetInnerHTML={{
              __html: from || '--'
            }}
            className={styles.value}
          ></span>
        ) : (
          <span className={styles.value}>{from || '--'}</span>
        )}
      </div>
      <div className={styles.detailsData}>
        <span className={styles.label}>To:</span>
        {typeof to === 'string' ? (
          <span
            dangerouslySetInnerHTML={{
              __html: to || '--'
            }}
            className={styles.value}
          ></span>
        ) : (
          <span className={styles.value}>{to || '--'}</span>
        )}
      </div>
      <Divider />
    </div>
  )
}
